/**
 * D&D 5th Edition — GameSystemAdapter Implementation
 *
 * Wraps existing @machina/core 5e data (class-progression, character-types,
 * spell-utils, ai-types) into the pluggable GameSystemAdapter interface.
 *
 * This is the reference implementation that other system plugins can follow.
 */
import { applyAffinity, buildWeaponDamage, getCharacterWeapons, getWeaponAttackBonus, getWeaponDamageNotation, getWeaponStats, getModifier, ALL_CLASS_NAMES, CLASS_HIT_DIE, CLASS_SAVE_PROFICIENCIES, SUBCLASS_LEVEL, CLASS_CASTER_TYPE, XP_THRESHOLDS, getSpellSlots, getSpellAttackBonus as coreSpellAttackBonus, getSpellSaveDc as coreSpellSaveDc, resolveSpellDamage, MagicLevelSchema, TechLevelSchema, } from '@machina/core';
import { findCantrip } from './cantrip-table.js';
// =============================================================================
// 5e CONSTANTS
// =============================================================================
const DND5E_ABILITIES = [
    { id: 'str', name: 'Strength', abbreviation: 'STR', description: 'Physical power, athletics, melee attacks' },
    { id: 'dex', name: 'Dexterity', abbreviation: 'DEX', description: 'Agility, reflexes, ranged attacks, AC' },
    { id: 'con', name: 'Constitution', abbreviation: 'CON', description: 'Health, stamina, hit points' },
    { id: 'int', name: 'Intelligence', abbreviation: 'INT', description: 'Knowledge, reasoning, arcane magic' },
    { id: 'wis', name: 'Wisdom', abbreviation: 'WIS', description: 'Perception, insight, divine magic' },
    { id: 'cha', name: 'Charisma', abbreviation: 'CHA', description: 'Force of personality, social skills, sorcery' },
];
const DND5E_CONDITIONS = [
    { id: 'blinded', name: 'Blinded', description: 'Cannot see. Auto-fail sight checks. Attacks have disadvantage, attacks against have advantage.', hasLevels: false },
    { id: 'charmed', name: 'Charmed', description: 'Cannot attack or target the charmer with harmful effects. Charmer has advantage on social checks.', hasLevels: false },
    { id: 'deafened', name: 'Deafened', description: 'Cannot hear. Auto-fail hearing checks.', hasLevels: false },
    { id: 'frightened', name: 'Frightened', description: 'Disadvantage on ability checks and attacks while source of fear is in line of sight. Cannot willingly move closer.', hasLevels: false },
    { id: 'grappled', name: 'Grappled', description: 'Speed becomes 0. Ends if grappler is incapacitated or forced apart.', hasLevels: false },
    { id: 'incapacitated', name: 'Incapacitated', description: 'Cannot take actions or reactions.', hasLevels: false },
    { id: 'invisible', name: 'Invisible', description: 'Cannot be seen without magic. Attacks have advantage, attacks against have disadvantage.', hasLevels: false },
    { id: 'paralyzed', name: 'Paralyzed', description: 'Incapacitated, cannot move or speak. Auto-fail STR/DEX saves. Attacks have advantage, melee hits are crits.', hasLevels: false },
    { id: 'petrified', name: 'Petrified', description: 'Turned to stone. Weight x10. Incapacitated. Resistant to all damage. Immune to poison/disease.', hasLevels: false },
    { id: 'poisoned', name: 'Poisoned', description: 'Disadvantage on attack rolls and ability checks.', hasLevels: false },
    { id: 'prone', name: 'Prone', description: 'Disadvantage on attacks. Melee attacks against have advantage, ranged have disadvantage. Must spend half movement to stand.', hasLevels: false },
    { id: 'restrained', name: 'Restrained', description: 'Speed 0. Attacks have disadvantage. Attacks against have advantage. Disadvantage on DEX saves.', hasLevels: false },
    { id: 'stunned', name: 'Stunned', description: 'Incapacitated, cannot move, can only speak falteringly. Auto-fail STR/DEX saves. Attacks against have advantage.', hasLevels: false },
    { id: 'unconscious', name: 'Unconscious', description: 'Incapacitated, cannot move or speak, unaware. Drop everything, fall prone. Auto-fail STR/DEX saves. Attacks have advantage, melee crits.', hasLevels: false },
    { id: 'exhaustion', name: 'Exhaustion', description: 'Cumulative levels of fatigue. Level 6 = death.', hasLevels: true, maxLevel: 6 },
    { id: 'concentrating', name: 'Concentrating', description: 'Maintaining a concentration spell. Taking damage requires CON save (DC 10 or half damage).', hasLevels: false },
    { id: 'bloodied', name: 'Bloodied', description: 'At or below 50% hit points. Not an official condition, but commonly used for narration.', hasLevels: false },
];
const DND5E_CLASSES = ALL_CLASS_NAMES.map(name => ({
    id: name.toLowerCase(),
    name,
    hitDie: CLASS_HIT_DIE[name],
    savingThrows: CLASS_SAVE_PROFICIENCIES[name],
    subclassLevel: SUBCLASS_LEVEL[name],
    spellcasting: CLASS_CASTER_TYPE[name],
    primaryAbility: getPrimaryAbility(name),
}));
/**
 * The least tech and magic a campaign needs before a class is offered.
 *
 * This is table curation, and it used to live in the creation wizard beside a
 * hand-copied class list. It belongs here because it is 5e content: whether a
 * setting without much magic has room for a Wizard is a question about 5e's
 * classes, and another system's classes need their own answer or none.
 */
const CLASS_SETTING_FLOOR = {
    Sorcerer: { minMagic: 'medium' },
    Warlock: { minMagic: 'medium' },
    Wizard: { minMagic: 'medium' },
    Artificer: { minTech: 'renaissance', minMagic: 'medium' },
};
// The schemas list their levels lowest first, so an index is a rank.
function atLeast(order, current, min) {
    if (!min || !current)
        return true;
    return order.indexOf(current) >= order.indexOf(min);
}
function classesForSetting(context) {
    if (!context)
        return DND5E_CLASSES;
    return DND5E_CLASSES.filter(cls => {
        const floor = CLASS_SETTING_FLOOR[cls.name];
        if (!floor)
            return true;
        return atLeast(TechLevelSchema.options, context.techLevel, floor.minTech)
            && atLeast(MagicLevelSchema.options, context.magicLevel, floor.minMagic);
    });
}
/**
 * Max hit die plus CON at 1st level, then the fixed average plus CON for each
 * level after. The creation wizard computed exactly this inline until it moved
 * here, and it is kept byte for byte so a new character gets the same number.
 *
 * Undefined without a class or a Constitution score: there is no hit die to
 * start from, and a CON of 10 made up here would be a real modifier on a sheet.
 */
function startingHitPoints(options) {
    const className = ALL_CLASS_NAMES.find(c => c.toLowerCase() === options.classId);
    const con = options.abilities?.con;
    if (!className || typeof con !== 'number')
        return undefined;
    const hitDie = CLASS_HIT_DIE[className];
    const conMod = getModifier(con);
    const level = Math.max(1, options.level ?? 1);
    const averageRoll = Math.ceil(hitDie / 2) + 1;
    return hitDie + conMod + (level - 1) * (averageRoll + conMod);
}
function getPrimaryAbility(className) {
    switch (className) {
        case 'Barbarian': return ['str'];
        case 'Bard': return ['cha'];
        case 'Cleric': return ['wis'];
        case 'Druid': return ['wis'];
        case 'Fighter': return ['str', 'dex'];
        case 'Monk': return ['dex', 'wis'];
        case 'Paladin': return ['str', 'cha'];
        case 'Ranger': return ['dex', 'wis'];
        case 'Rogue': return ['dex'];
        case 'Sorcerer': return ['cha'];
        case 'Warlock': return ['cha'];
        case 'Wizard': return ['int'];
        case 'Artificer': return ['int'];
    }
}
// =============================================================================
// RULES DEFINITION
// =============================================================================
export const DND5E_RULES = {
    name: 'D&D 5th Edition',
    shortName: '5e',
    license: 'CC-BY-4.0 (SRD 5.1)',
    publisher: 'Wizards of the Coast',
    abilities: DND5E_ABILITIES,
    abilityModFormula: 'floor((score - 10) / 2)',
    characterTemplate: {
        levelCap: 20,
        usesClasses: true,
        usesRaces: true,
        progressionType: 'xp',
        xpTable: [...XP_THRESHOLDS],
        healthModel: 'hit-points',
        defenseModel: 'armor-class',
    },
    classes: DND5E_CLASSES,
    coreResolution: {
        type: 'd20-roll-high',
        critRange: [20],
        fumbleRange: [1],
    },
    combat: {
        initiativeFormula: '1d20 + dex_mod',
        actionEconomy: [
            { id: 'action', name: 'Action', countPerRound: 1 },
            { id: 'bonus_action', name: 'Bonus Action', countPerRound: 1 },
            { id: 'reaction', name: 'Reaction', countPerRound: 1 },
            { id: 'movement', name: 'Movement', description: 'Move up to speed in feet', countPerRound: 1 },
            { id: 'free', name: 'Free Action', description: 'Object interaction, short communication', countPerRound: 1 },
        ],
        roundDuration: '6 seconds',
        gridBased: true,
        gridUnit: '5ft',
        deathMechanic: 'death-saves',
    },
    conditions: DND5E_CONDITIONS,
    magic: {
        type: 'spell-slots',
        castingAbilities: ['int', 'wis', 'cha'],
        concentrationRule: true,
    },
    encounterBalance: {
        ratingSystem: 'cr',
        description: 'Challenge Rating (CR) measures relative encounter difficulty against a party of 4.',
    },
    dmGuidance: {
        systemBrief: `D&D 5th Edition is a d20-based fantasy TTRPG. Characters have 6 ability scores (STR, DEX, CON, INT, WIS, CHA) with modifiers derived from (score - 10) / 2. Most checks are 1d20 + modifier vs a Difficulty Class (DC). Combat uses initiative order, action economy (action + bonus action + reaction per round), and hit points. Magic users have spell slots that deplete and recover on rests.`,
        combatNorms: 'Combat rounds are 6 seconds. Each creature gets one turn per round with an action, bonus action, reaction, movement, and free object interaction. Attacks roll 1d20 + modifier vs target AC. Damage reduces HP. At 0 HP, characters make death saves (3 successes = stable, 3 failures = death). Natural 20 on an attack is a critical hit (double damage dice).',
        socialNorms: 'Social encounters use Charisma-based skills (Persuasion, Deception, Intimidation, Performance). DCs typically range from 10 (easy) to 25 (nearly impossible). Insight opposes Deception. NPCs have dispositions that affect DC. Roleplaying may grant advantage.',
        toneGuidance: '5e is designed for heroic fantasy. Characters are exceptional from level 1 and become legendary by level 15+. Death is possible but not frequent. The tone supports both serious drama and lighthearted adventure.',
        commonMistakes: [
            'Don\'t forget concentration checks when a spellcaster takes damage (DC 10 or half damage, whichever is higher)',
            'Bonus actions are NOT extra actions — each class/spell specifies what can be done as a bonus action',
            'Opportunity attacks only trigger on leaving melee range, not on moving within it',
            'Short rests are 1 hour, long rests are 8 hours. Many abilities recharge on short rest.',
            'Proficiency bonus is level-based (+2 at 1st through +6 at 17th), not class-based',
            'Advantage/disadvantage do NOT stack — multiple sources still just roll 2d20',
        ],
    },
};
const defaultRng = Math.random;
function rollD20(rng = defaultRng) {
    return Math.floor(rng() * 20) + 1;
}
/**
 * Roll the d20 with 5e advantage rules applied.
 *
 * Advantage keeps the higher of 2d20, disadvantage the lower, and holding both
 * at once cancels down to a flat 1d20 — they do not stack and they do not
 * accumulate, however many sources are in play.
 */
function rollD20Advantaged(wantAdvantage, wantDisadvantage, rng = defaultRng) {
    const advantage = !!wantAdvantage && !wantDisadvantage;
    const disadvantage = !!wantDisadvantage && !wantAdvantage;
    const dice = advantage || disadvantage ? [rollD20(rng), rollD20(rng)] : [rollD20(rng)];
    const natural = advantage
        ? Math.max(...dice)
        : disadvantage
            ? Math.min(...dice)
            : dice[0];
    return { dice, natural, advantage, disadvantage };
}
/** `2d20kh1` / `2d20kl1` / `1d20`, plus the modifier if there is one. */
function d20Formula(roll, modifier) {
    let formula = roll.advantage ? '2d20kh1' : roll.disadvantage ? '2d20kl1' : '1d20';
    if (modifier !== 0) {
        formula += modifier > 0 ? ` + ${modifier}` : ` - ${Math.abs(modifier)}`;
    }
    return formula;
}
/** Conditions on the attacker that impose disadvantage on its attack rolls. */
const ATTACKER_DISADVANTAGE_CONDITIONS = new Set([
    'blinded', 'frightened', 'poisoned', 'prone', 'restrained',
]);
/** Conditions on the target that grant advantage to attacks against it. */
const TARGET_ADVANTAGE_CONDITIONS = new Set([
    'blinded', 'paralyzed', 'petrified', 'restrained', 'stunned', 'unconscious',
]);
/** Conditions on the target that impose disadvantage on attacks against it. */
const TARGET_DISADVANTAGE_CONDITIONS = new Set(['invisible']);
function hasCondition(combatant, ids) {
    return (combatant.character?.conditions ?? []).some(c => ids.has(c.conditionId));
}
/**
 * Work out whether an attack is made with advantage or disadvantage.
 *
 * Explicit `meta.advantage` / `meta.disadvantage` on the attacker wins — that
 * is the caller stating a circumstance the adapter cannot see (flanking, help,
 * cover, a class feature). Otherwise it is read off the conditions in play.
 *
 * Prone is treated as a melee attack against the target, which is the common
 * case in this engine; a ranged attacker against a prone target should pass
 * `meta.disadvantage` explicitly.
 */
function attackAdvantage(attacker, target) {
    const explicitAdv = attacker.meta?.advantage;
    const explicitDis = attacker.meta?.disadvantage;
    if (typeof explicitAdv === 'boolean' || typeof explicitDis === 'boolean') {
        return { advantage: !!explicitAdv, disadvantage: !!explicitDis };
    }
    const advantage = hasCondition(target, TARGET_ADVANTAGE_CONDITIONS) ||
        hasCondition(target, new Set(['prone'])) ||
        hasCondition(attacker, new Set(['invisible']));
    const disadvantage = hasCondition(attacker, ATTACKER_DISADVANTAGE_CONDITIONS) ||
        hasCondition(target, TARGET_DISADVANTAGE_CONDITIONS);
    return { advantage, disadvantage };
}
function rollDice(count, sides, rng = defaultRng) {
    return Array.from({ length: count }, () => Math.floor(rng() * sides) + 1);
}
function getProficiencyBonus(level) {
    return Math.ceil(1 + level / 4);
}
function getHpStatus(current, max) {
    if (current <= 0)
        return 'unconscious';
    const ratio = current / max;
    if (ratio > 0.75)
        return 'healthy';
    if (ratio > 0.5)
        return 'wounded';
    if (ratio > 0.25)
        return 'bloodied';
    return 'critical';
}
// =============================================================================
// ATTACK DERIVATION
// =============================================================================
/**
 * `SystemCharacter.abilities` is keyed by the adapter's own ability ids (`str`,
 * `dex`), while `weapon-utils` predates the adapter and is keyed by full names.
 * Translating here rather than changing either side: the short ids are the
 * adapter contract and the long names are all over the character sheet, so the
 * one thing that must not happen is a third spelling.
 */
const ABILITY_LONG_NAME = {
    str: 'strength', dex: 'dexterity', con: 'constitution',
    int: 'intelligence', wis: 'wisdom', cha: 'charisma',
};
function fullAbilityScores(abilities) {
    const out = {
        strength: 10, dexterity: 10, constitution: 10,
        intelligence: 10, wisdom: 10, charisma: 10,
    };
    for (const [key, value] of Object.entries(abilities ?? {})) {
        const long = ABILITY_LONG_NAME[key.toLowerCase()] ?? key.toLowerCase();
        if (long in out)
            out[long] = value;
    }
    return out;
}
/** The reverse of `ABILITY_LONG_NAME`, so a caller may hand us either spelling. */
const ABILITY_SHORT_NAME = Object.fromEntries(Object.entries(ABILITY_LONG_NAME).map(([short, long]) => [long, short]));
/**
 * Read one ability score without caring which spelling the caller used.
 *
 * The adapter contract is short ids (`wis`), and that is what everything in
 * this file writes. But `magic.ability` is declared by whoever packed the
 * character — a bridge, a stat block, an imported sheet — and `"wisdom"` is
 * the spelling on the character sheet. Accepting both here is cheaper than a
 * third spelling appearing somewhere downstream. Returns `undefined` when the
 * score genuinely is not there; a missing score is not a 10.
 */
function abilityScore(abilities, key) {
    const want = key.trim().toLowerCase();
    if (!want)
        return undefined;
    const long = ABILITY_LONG_NAME[want] ?? want;
    const short = ABILITY_SHORT_NAME[long] ?? want;
    for (const [rawKey, value] of Object.entries(abilities ?? {})) {
        const k = rawKey.trim().toLowerCase();
        if ((k === want || k === long || k === short) && typeof value === 'number')
            return value;
    }
    return undefined;
}
/** Total level across all classes, which is what cantrips scale on. */
function totalCharacterLevel(character) {
    if (typeof character.level === 'number' && character.level > 0)
        return character.level;
    const summed = (character.classes ?? []).reduce((sum, c) => sum + (c.level ?? 0), 0);
    return summed > 0 ? summed : 1;
}
/**
 * Everything the three magic methods need, or nothing.
 *
 * Returning `null` is the whole point. A character with no declared casting
 * ability cannot be given one here — that is exactly the bug this replaces,
 * where the battle sim assumed WIS and handed a sorcerer a cleric's save DC.
 * The honest answer to "what is this character's spell save DC" when nobody
 * said what they cast with is that we do not know, and `undefined` says so.
 */
function castingProfile(character) {
    const magic = (character.magic ?? undefined);
    const ability = typeof magic?.ability === 'string' ? magic.ability : '';
    if (!ability.trim())
        return null;
    const score = abilityScore(character.abilities, ability);
    if (score === undefined)
        return null;
    const level = totalCharacterLevel(character);
    return {
        abilityMod: getModifier(score),
        proficiency: getProficiencyBonus(level),
        level,
        features: (magic?.features ?? []).filter((f) => typeof f === 'string').map(name => ({ name })),
        magic: magic ?? {},
    };
}
/** Spell names this character can actually reach for: known plus prepared, deduped. */
function castableSpellNames(magic) {
    const prepared = Array.isArray(magic.prepared)
        ? magic.prepared
        : magic.prepared && typeof magic.prepared === 'object'
            ? Object.keys(magic.prepared)
            : [];
    const seen = new Set();
    const out = [];
    for (const name of [...(magic.known ?? []), ...prepared]) {
        if (typeof name !== 'string')
            continue;
        const key = name.trim().toLowerCase();
        if (!key || seen.has(key))
            continue;
        seen.add(key);
        out.push(name);
    }
    return out;
}
/** Equipment rides on the combatant, because `SystemCharacter` has no slot for it. */
function equipmentOf(combatant) {
    const fromMeta = combatant.meta?.equipment;
    if (Array.isArray(fromMeta))
        return fromMeta.filter((i) => typeof i === 'string');
    const fromChar = combatant.character?.systemData?.equipment;
    if (Array.isArray(fromChar))
        return fromChar.filter((i) => typeof i === 'string');
    return [];
}
function normalizeAction(action) {
    return typeof action === 'string' ? { name: action } : action;
}
/**
 * Find the weapon behind an attack.
 *
 * Named first — "Longsword", "+1 Rapier", "Flametongue (Longsword)" all parse —
 * because the caller naming a weapon is the caller telling us which one. Only
 * when the name means nothing do we fall back to the attacker's equipment, and
 * then to whichever carried weapon matches the action's declared range.
 */
function weaponForAction(attacker, action) {
    const named = getWeaponStats(action.name);
    if (named)
        return named;
    const carried = getCharacterWeapons(equipmentOf(attacker));
    if (carried.length === 0)
        return null;
    if (action.range === 'melee' || action.range === 'ranged') {
        const matched = carried.find(w => w.range === action.range);
        if (matched)
            return matched;
    }
    return carried[0];
}
/** `2d6+3`, `1d8`, `1d4 - 1`, or a flat `3`. Returns null on anything else. */
function parseDiceExpression(expr) {
    const trimmed = expr.trim();
    const dice = trimmed.match(/^(\d*)\s*d\s*(\d+)\s*(?:([+-])\s*(\d+))?$/i);
    if (dice) {
        const modifier = dice[3] ? (dice[3] === '-' ? -1 : 1) * parseInt(dice[4], 10) : 0;
        return { count: dice[1] ? parseInt(dice[1], 10) : 1, sides: parseInt(dice[2], 10), modifier };
    }
    const flat = trimmed.match(/^([+-]?\d+)$/);
    if (flat)
        return { count: 0, sides: 0, modifier: parseInt(flat[1], 10) };
    return null;
}
/**
 * Roll a damage expression, applying 5e's crit rule: DOUBLE THE DICE, ADD THE
 * MODIFIER ONCE.
 *
 * This is the rule the engine has been getting wrong in two places at once.
 * `useBattleSim` rolled the whole expression twice, paying the ability modifier
 * a second time; this adapter rolled a flat 1d8 and then added STR to the
 * doubled result. Doomsworn's adapter is the only implementation that had it
 * right. An expression with no dice — an unarmed strike, a flat-damage weapon —
 * has nothing to double, so a crit leaves it unchanged. That reads as a bug and
 * is not one.
 */
function rollDamageExpression(expr, isCrit, rng) {
    const count = isCrit ? expr.count * 2 : expr.count;
    const rolled = count > 0 ? rollDice(count, expr.sides, rng).reduce((a, b) => a + b, 0) : 0;
    const notation = count > 0 ? `${count}d${expr.sides}` : `${expr.modifier}`;
    // 5e floors damage at 0, not at 1: a blow can be shrugged off entirely.
    return { total: Math.max(0, rolled + expr.modifier), notation };
}
/**
 * Roll the damage for a landed attack.
 *
 * Three sources, most specific first. A declared `damageDice` is the caller's
 * own number — a monster's stat-block action — and is rolled as written. A
 * weapon is looked up in the SRD table, so finesse, versatile and a magic
 * bonus all count. Failing both, it is an unarmed strike: 1 + STR bludgeoning,
 * which is the actual 5e rule and not the `1d1+STR` the battle sim invented.
 */
function rollAttackDamage(spec, weapon, abilities, isCrit, rng) {
    let expr = null;
    let type = spec.damageType;
    let magical = spec.magical;
    if (spec.damageDice) {
        expr = parseDiceExpression(spec.damageDice);
    }
    if (!expr && weapon) {
        const wd = buildWeaponDamage(weapon, abilities, getModifier, isCrit, spec.versatile ?? false);
        if (wd) {
            expr = { count: wd.dieCount, sides: wd.dieSize, modifier: wd.modifier };
            type ??= wd.damageType;
            magical ??= weapon.magicBonus > 0;
        }
    }
    if (!expr) {
        expr = { count: 0, sides: 0, modifier: 1 + getModifier(abilities.strength) };
        type ??= 'bludgeoning';
    }
    const { total, notation } = rollDamageExpression(expr, isCrit, rng);
    const resolvedType = type ?? 'bludgeoning';
    const component = { amount: total, type: resolvedType, dice: notation, magical };
    return { total, type: resolvedType, dice: notation, magical, components: [component] };
}
/**
 * Every attack this combatant could make with what it is carrying.
 *
 * The numbers are the same ones `resolveAttack` would derive, computed the same
 * way, so a caller can rank these and then hand the winner straight back. An
 * empty-handed combatant still gets one entry: everyone can punch.
 */
function listWeaponAttacks(combatant) {
    const char = combatant.character;
    const abilities = fullAbilityScores(char?.abilities);
    const proficiency = getProficiencyBonus(char?.level ?? 1);
    const attacks = getCharacterWeapons(equipmentOf(combatant)).map(weapon => ({
        name: weapon.name,
        attackBonus: getWeaponAttackBonus(weapon, abilities, proficiency, getModifier),
        damageDice: getWeaponDamageNotation(weapon, abilities, getModifier),
        damageType: weapon.damageType,
        range: weapon.range,
        magical: weapon.magicBonus > 0,
    }));
    if (attacks.length > 0)
        return attacks;
    const strMod = getModifier(abilities.strength);
    return [{
            name: 'Unarmed Strike',
            attackBonus: strMod + proficiency,
            damageDice: `${1 + strMod}`,
            damageType: 'bludgeoning',
            range: 'melee',
        }];
}
// =============================================================================
// THE ADAPTER
// =============================================================================
export const dnd5eAdapter = {
    id: 'dnd-5e',
    name: 'D&D 5th Edition',
    version: '0.1.0',
    rules: DND5E_RULES,
    // --- Character Lifecycle ---
    createCharacter(options) {
        const abilities = options.abilities ?? {
            str: 10, dex: 10, con: 10, int: 10, wis: 10, cha: 10,
        };
        const level = options.level ?? 1;
        const classId = options.classId ?? 'fighter';
        const className = ALL_CLASS_NAMES.find(c => c.toLowerCase() === classId) ?? 'Fighter';
        const hitDie = CLASS_HIT_DIE[className] ?? 8;
        const conMod = getModifier(abilities.con ?? 10);
        const maxHp = hitDie + conMod;
        return {
            id: crypto.randomUUID(),
            name: options.name,
            systemId: 'dnd-5e',
            abilities,
            level,
            classes: [{ classId, level, subclass: undefined }],
            health: { current: maxHp, max: maxHp, temp: 0 },
            defense: 10 + getModifier(abilities.dex ?? 10), // base AC = 10 + DEX mod
            speed: 30,
            conditions: [],
            proficiencies: {
                savingThrows: CLASS_SAVE_PROFICIENCIES[className] ?? [],
                skills: [],
            },
            magic: CLASS_CASTER_TYPE[className] !== 'none' ? {
                casterType: CLASS_CASTER_TYPE[className],
                slots: getSpellSlots(className, level),
            } : undefined,
        };
    },
    validateCharacter(character) {
        const errors = [];
        const warnings = [];
        if (!character.abilities || Object.keys(character.abilities).length === 0) {
            errors.push('Character must have ability scores');
        }
        for (const [ability, score] of Object.entries(character.abilities)) {
            if (score < 1 || score > 30) {
                errors.push(`${ability}: score ${score} out of range (1-30)`);
            }
        }
        if (character.level !== undefined) {
            if (character.level < 1 || character.level > 20) {
                errors.push(`Level ${character.level} out of range (1-20)`);
            }
        }
        if (character.classes) {
            const totalLevel = character.classes.reduce((sum, c) => sum + c.level, 0);
            if (totalLevel > 20) {
                errors.push(`Total class levels (${totalLevel}) exceed maximum (20)`);
            }
        }
        if (character.health.max <= 0) {
            errors.push('Max HP must be positive');
        }
        return { valid: errors.length === 0, errors, warnings };
    },
    getAbilityModifier(score) {
        return getModifier(score);
    },
    getAbilities() {
        return DND5E_ABILITIES;
    },
    getClasses(context) {
        return classesForSetting(context);
    },
    getConditions() {
        return DND5E_CONDITIONS;
    },
    levelUp(character, choices) {
        const updated = { ...character };
        const classId = choices.classId;
        const className = ALL_CLASS_NAMES.find(c => c.toLowerCase() === classId) ?? 'Fighter';
        const hitDie = CLASS_HIT_DIE[className] ?? 8;
        const conMod = getModifier(character.abilities.con ?? 10);
        // Increment class level
        const classes = [...(updated.classes ?? [])];
        const existing = classes.find(c => c.classId === classId);
        if (existing) {
            existing.level += 1;
        }
        else {
            classes.push({ classId, level: 1, subclass: choices.subclass });
        }
        updated.classes = classes;
        // Update total level
        const newLevel = classes.reduce((sum, c) => sum + c.level, 0);
        updated.level = newLevel;
        // HP: average + CON mod
        const hpGain = Math.max(1, Math.ceil(hitDie / 2) + 1 + conMod);
        updated.health = {
            ...updated.health,
            max: updated.health.max + hpGain,
            current: updated.health.current + hpGain,
        };
        // Apply ASI
        if (choices.abilityScoreImprovements) {
            const newAbilities = { ...updated.abilities };
            for (const [ability, increase] of Object.entries(choices.abilityScoreImprovements)) {
                newAbilities[ability] = Math.min(20, (newAbilities[ability] ?? 10) + increase);
            }
            updated.abilities = newAbilities;
        }
        return updated;
    },
    getProficiencyBonus(level) {
        return getProficiencyBonus(level);
    },
    // --- Dice & Resolution ---
    rollCheck(context) {
        const modifier = context.modifier ?? 0;
        const roll = rollD20Advantaged(context.advantage, context.disadvantage);
        const total = roll.natural + modifier;
        return {
            total,
            dice: roll.dice,
            modifier,
            formula: d20Formula(roll, modifier),
            natural: roll.natural,
            meta: {
                type: context.type,
                ability: context.ability,
                skill: context.skill,
                dc: context.dc,
                // Normalized, not echoed: asking for both cancels to a flat d20 and the
                // meta must say so, or the log disagrees with the dice.
                advantage: roll.advantage,
                disadvantage: roll.disadvantage,
            },
        };
    },
    interpretResult(roll, context) {
        const dc = context.dc;
        // A natural 20 is a critical whether or not anyone named a difficulty —
        // criticality is a question about the die, and the two crit branches below
        // are answered before the target ever matters. That is why they sit above
        // the undecided check rather than below it.
        if (roll.natural === 20) {
            return {
                outcome: 'critical-success',
                description: dc === undefined
                    ? `Natural 20! Critical success (total ${roll.total})`
                    : `Natural 20! Critical success (total ${roll.total} vs DC ${dc})`,
            };
        }
        if (roll.natural === 1) {
            return {
                outcome: 'critical-failure',
                description: dc === undefined
                    ? `Natural 1! Critical failure (total ${roll.total})`
                    : `Natural 1! Critical failure (total ${roll.total} vs DC ${dc})`,
            };
        }
        // No DC means no verdict. This used to default to 10 and report
        // "Success (14 vs DC 10)" against a difficulty nobody had set — a confident
        // sentence about a comparison that never happened.
        if (dc === undefined) {
            return {
                outcome: 'undecided',
                description: `Rolled ${roll.total} — no DC was set, so this roll has not been judged.`,
                meta: { undecided: true, reason: 'no-target' },
            };
        }
        if (roll.total >= dc) {
            return {
                outcome: 'success',
                description: `Success (${roll.total} vs DC ${dc})`,
            };
        }
        return {
            outcome: 'failure',
            description: `Failure (${roll.total} vs DC ${dc})`,
        };
    },
    // --- Combat ---
    combat: {
        rollInitiative(combatant) {
            const dexMod = combatant.character
                ? getModifier(combatant.character.abilities.dex ?? 10)
                : 0;
            const natural = rollD20();
            return natural + dexMod;
        },
        getActionEconomy(_combatant) {
            return {
                actions: [
                    { typeId: 'action', remaining: 1, options: ['Attack', 'Cast a Spell', 'Dash', 'Disengage', 'Dodge', 'Help', 'Hide', 'Ready', 'Search', 'Use an Object'] },
                    { typeId: 'bonus_action', remaining: 1, options: [] }, // class-specific
                    { typeId: 'reaction', remaining: 1, options: ['Opportunity Attack'] },
                ],
            };
        },
        listAttacks(combatant) {
            return listWeaponAttacks(combatant);
        },
        /**
         * Resolve one attack.
         *
         * WHAT CHANGED AND WHY (ISS-0151). This used to add the attacker's STRENGTH
         * modifier whatever they were holding, ignore the `action` argument
         * entirely, and deal a literal `1d8 slashing` — a comment in the code called
         * it "simplified". That is not a simplification of 5e, it is a different
         * game, and it is the reason combat could not be moved onto the adapter:
         * routing `useBattleSim` through it would have made every monster in the
         * bestiary hit for 1d8 + STR.
         *
         * The numbers now come from, in order: what the CALLER declared on the
         * action, then the WEAPON behind it, then an unarmed strike. A monster's
         * stat-block action carries its own attack bonus and damage dice and is
         * believed; a player character's longsword is looked up in the SRD weapon
         * table, which knows about finesse, versatile and magic bonuses.
         *
         * The hit/miss VERDICT is delegated to `interpretResult` rather than
         * recomputed here, so there is one place in this adapter that decides what
         * beats an AC — including the nat-20 reading ISS-0150 has open. The wording
         * is still the combat wording, because "Hit! (18 vs AC 15)" and "Success
         * (18 vs DC 15)" are the same verdict said to two different audiences.
         */
        resolveAttack(attacker, target, action, options) {
            const rng = options?.rng ?? defaultRng;
            const spec = normalizeAction(action);
            const char = attacker.character;
            const abilities = fullAbilityScores(char?.abilities);
            const proficiency = getProficiencyBonus(char?.level ?? 1);
            const weapon = weaponForAction(attacker, spec);
            const atkMod = spec.attackBonus ??
                (weapon
                    ? getWeaponAttackBonus(weapon, abilities, proficiency, getModifier)
                    : getModifier(abilities.strength) + proficiency);
            const { advantage, disadvantage } = attackAdvantage(attacker, target);
            const d20 = rollD20Advantaged(advantage, disadvantage, rng);
            const natural = d20.natural;
            const total = natural + atkMod;
            const targetAC = target.character?.defense ?? 10;
            const roll = {
                total, dice: d20.dice, modifier: atkMod,
                formula: d20Formula(d20, atkMod), natural,
                meta: { type: 'attack', dc: targetAC, advantage: d20.advantage, disadvantage: d20.disadvantage },
            };
            const verdict = dnd5eAdapter.interpretResult(roll, {
                type: 'attack',
                dc: targetAC,
                modifier: atkMod,
                advantage: d20.advantage,
                disadvantage: d20.disadvantage,
            });
            const isCrit = verdict.outcome === 'critical-success';
            const hit = isCrit || verdict.outcome === 'success';
            const swing = d20.advantage ? ' with advantage' : d20.disadvantage ? ' with disadvantage' : '';
            const interpretation = {
                outcome: verdict.outcome,
                description: hit
                    ? `Hit!${swing} (${total} vs AC ${targetAC}${isCrit ? ' — Critical!' : ''})`
                    : `Miss${swing} (${total} vs AC ${targetAC})`,
                // `range` is reported because the CALLER usually cannot work it out.
                // A caller that hands over `{ name: 'Attack' }` and lets this adapter
                // choose the weapon has, by construction, no idea whether the answer
                // was a rapier or a longbow — and a battle map has to know that before
                // it walks a token into melee. The weapon's own range wins; a spec that
                // declared one (a spell attack) keeps it.
                meta: {
                    ...(verdict.meta ?? {}),
                    weapon: weapon?.name ?? spec.name,
                    range: spec.range ?? weapon?.range ?? 'melee',
                },
            };
            if (!hit)
                return { hit, roll, interpretation };
            const damage = rollAttackDamage(spec, weapon, abilities, isCrit, rng);
            return { hit, roll, interpretation, damage };
        },
        /**
         * Work out what this damage would do — resistances included.
         *
         * The affinity lives on the COMBATANT (`damageAffinity`), which is what
         * ISS-0151 called "somewhere to carry resistances": the things with
         * resistances here are monsters, and a monster is never a SystemCharacter.
         * Before this, `damage.type` was accepted and then never read, in every
         * adapter — a signature that asked for a damage type and had no opinion
         * about it.
         *
         * The triggered effects key off what ACTUALLY landed, not what was thrown.
         * A creature immune to fire takes 0 from a fireball and makes no death
         * saves, however large the number on the front of it was.
         */
        applyDamage(target, damage) {
            const char = target.character;
            const { amount: applied, verdict: affinity } = applyAffinity(target.damageAffinity, damage.total, { type: damage.type, magical: damage.magical });
            if (!char) {
                return {
                    previousHp: 0, currentHp: 0, status: 'dead', effects: [],
                    incomingDamage: damage.total, appliedDamage: applied, affinity,
                };
            }
            const previousHp = char.health.current;
            const newHp = Math.max(0, previousHp - applied);
            // Deliberately NOT written back to the character. The engine owns state
            // (ISS-0132): an adapter answers "what would this damage do", and the
            // caller decides whether it happened. previousHp/currentHp below carry
            // everything needed to apply it, so the write was redundant as well as
            // forbidden. Enforced by @machina/system-conformance, which caught this.
            const effects = [];
            if (newHp === 0 && previousHp > 0) {
                effects.push('death-saves');
                if (applied >= char.health.max) {
                    effects.push('massive-damage');
                }
            }
            return {
                previousHp,
                currentHp: newHp,
                status: getHpStatus(newHp, char.health.max),
                effects,
                incomingDamage: damage.total,
                appliedDamage: applied,
                affinity,
            };
        },
    },
    // --- Magic ---
    magic: {
        getSpellSlots(character) {
            if (!character.classes || !character.magic) {
                return {};
            }
            const primaryClass = character.classes[0];
            if (!primaryClass)
                return {};
            const className = ALL_CLASS_NAMES.find(c => c.toLowerCase() === primaryClass.classId);
            if (!className)
                return {};
            const rawSlots = getSpellSlots(className, primaryClass.level);
            const slots = {};
            for (const [levelStr, count] of Object.entries(rawSlots)) {
                const level = parseInt(levelStr, 10);
                if (!isNaN(level) && count > 0) {
                    slots[level] = { total: count, used: 0 };
                }
            }
            return { slots };
        },
        validateCast(character, _spellName, level) {
            if (!character.magic) {
                return { canCast: false, reason: 'Character has no spellcasting ability' };
            }
            if (level === 0) {
                return { canCast: true }; // Cantrips always work
            }
            // Check if slot available (simplified)
            return { canCast: true, warnings: [] };
        },
        resolveCast(_character, spellName, _targets) {
            return {
                success: true,
                effects: [`Cast ${spellName}`],
            };
        },
        /**
         * 5e's spell save DC: 8 + proficiency + spellcasting ability modifier.
         *
         * The formula was already in core as `getSpellSaveDc` with no callers,
         * while the battle sim computed it inline and hardcoded WIS. The ability
         * now comes from `magic.ability` and nowhere else — if the caller did not
         * say, this returns `undefined` rather than picking one, because a guessed
         * DC is indistinguishable from a ruled one once it reaches the table.
         */
        getSpellSaveDC(character) {
            const profile = castingProfile(character);
            if (!profile)
                return undefined;
            return coreSpellSaveDc(profile.abilityMod, profile.proficiency);
        },
        /** Proficiency + spellcasting ability modifier, on the same terms. */
        getSpellAttackBonus(character) {
            const profile = castingProfile(character);
            if (!profile)
                return undefined;
            return coreSpellAttackBonus(profile.abilityMod, profile.proficiency);
        },
        /**
         * Every spell attack this caster can make, resolved at their current level.
         *
         * Matched against `SRD_CANTRIPS`, because the damage rules live in the
         * spell's prose and the only other source of that prose is an async fetch
         * (see `cantrip-table.ts`). Save cantrips are excluded: Poison Spray deals
         * damage but is not an attack.
         *
         * ONE ENTRY PER ROLL. Eldritch Blast at 11th is three `CombatAction`s, not
         * one `3d10`, because three beams are three attack rolls against
         * potentially three targets and Agonizing Blast pays its bonus on each one.
         * Folding them together would change both the hit odds and the damage.
         */
        listSpellAttacks(character) {
            const profile = castingProfile(character);
            if (!profile)
                return [];
            const attackBonus = coreSpellAttackBonus(profile.abilityMod, profile.proficiency);
            const actions = [];
            for (const spellName of castableSpellNames(profile.magic)) {
                const entry = findCantrip(spellName);
                if (!entry || entry.attackType === null)
                    continue;
                const resolved = resolveSpellDamage({
                    spellName: entry.name,
                    description: entry.description,
                    characterLevel: profile.level,
                    abilityMod: profile.abilityMod,
                    features: profile.features,
                });
                if (!resolved)
                    continue;
                const bonus = resolved.bonusPerHit;
                const damageDice = bonus === 0
                    ? resolved.dicePerHit
                    : `${resolved.dicePerHit}${bonus > 0 ? '+' : ''}${bonus}`;
                for (let i = 0; i < Math.max(1, resolved.hitCount); i++) {
                    actions.push({
                        // Numbered only when there is more than one, so a single-roll
                        // cantrip reads as itself in a combat log.
                        name: resolved.hitCount > 1 ? `${entry.name} (beam ${i + 1})` : entry.name,
                        attackBonus,
                        damageDice,
                        damageType: resolved.damageType,
                        range: 'spell',
                        magical: true,
                    });
                }
            }
            return actions;
        },
    },
    // --- Encounter Balance ---
    balance: {
        rateEncounter(party, enemies) {
            const partyLevel = party.reduce((sum, c) => sum + (c.level ?? 1), 0) / party.length;
            const enemyCount = enemies.length;
            // Simplified CR-based rating
            const ratio = enemyCount / party.length;
            if (ratio <= 0.5)
                return { label: 'Easy', numericValue: 1, description: `${enemyCount} enemies vs ${party.length} party members (avg level ${partyLevel.toFixed(0)})` };
            if (ratio <= 1)
                return { label: 'Medium', numericValue: 2, description: `Balanced encounter` };
            if (ratio <= 1.5)
                return { label: 'Hard', numericValue: 3, description: `Challenging encounter` };
            return { label: 'Deadly', numericValue: 4, description: `Potentially lethal encounter`, warnings: ['Consider providing escape routes'] };
        },
        suggestEncounter(_party, _difficulty) {
            return []; // Would query bestiary
        },
    },
    // --- Character Surface ---
    //
    // No creation spec or sheet sections: the six abilities and the standard
    // array are the defaults every generic surface already falls back to.
    characterSurface: {
        getStartingHealth: startingHitPoints,
    },
    // --- DM Guidance ---
    getDmGuidance() {
        return DND5E_RULES.dmGuidance;
    },
};
//# sourceMappingURL=dnd5e-adapter.js.map